import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { statusLabels } from "../../../utils/officeScheme/officeSchemeStatus";
import { generateOfficeReceiptPDF } from "../../../utils/officeScheme/generateOfficeReceiptPDF";
import { generateOverlayEstimatePDF } from "../../../utils/officeScheme/generateOverlayEstimatePDF";

export default function OfficeApplications() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "office_scheme_applications"));
      setApps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      alert(`Applications fetch error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const updateStatus = async (app, status) => {
    const payload = {
      status,
      updatedAt: serverTimestamp(),
    };

    if (status === "PAYMENT_DONE") {
      payload.paymentStatus = "PAID";
      payload.paidAt = serverTimestamp();
    }

    await updateDoc(doc(db, "office_scheme_applications", app.id), payload);
    await fetchApps();
    setSelected(null);
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <div className="flex justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold">Applications</h2>
          <p className="text-sm text-slate-500">
            PACS / Society submitted applications
          </p>
        </div>

        <button
          onClick={fetchApps}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {apps.length === 0 ? (
        <div className="border rounded-xl p-6 text-center text-slate-500">
          No applications found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-slate-100">
              <tr>
                <th className="border p-2">Society</th>
                <th className="border p-2">District</th>
                <th className="border p-2">Design</th>
                <th className="border p-2">Estimate</th>
                <th className="border p-2">Payment</th>
                <th className="border p-2">Status</th>
                <th className="border p-2">Action</th>
              </tr>
            </thead>

            <tbody>
              {apps.map((a) => (
                <tr key={a.id}>
                  <td className="border p-2 font-bold">
                    {a.societyName || "-"}
                  </td>
                  <td className="border p-2">{a.district || "-"}</td>
                  <td className="border p-2">
                    {a.selectedDesign?.sizeLabel || "-"}
                  </td>
                  <td className="border p-2">₹ {a.estimateAmount || 0}</td>
                  <td className="border p-2">
                    {a.paymentStatus || "PENDING"}
                  </td>
                  <td className="border p-2">
                    {statusLabels[a.status] || a.status || "-"}
                  </td>
                  <td className="border p-2">
                    <button
                      onClick={() => setSelected(a)}
                      className="bg-emerald-600 text-white px-3 py-1 rounded"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-6xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between border-b pb-3 mb-5">
              <div>
                <h2 className="text-2xl font-bold">
                  {selected.societyName || "-"}
                </h2>
                <p className="text-sm text-slate-500">
                  {selected.registrationNo || "-"}
                </p>
              </div>

              <button
                onClick={() => setSelected(null)}
                className="bg-slate-200 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Info label="Registration No" value={selected.registrationNo} />
              <Info label="Address" value={selected.address} />
              <Info label="Village" value={selected.village} />
              <Info label="Taluka" value={selected.taluka} />
              <Info label="District" value={selected.district} />
              <Info label="Mobile" value={selected.mobile} />
              <Info label="Email" value={selected.email} />
              <Info
                label="Plot Area"
                value={`${selected.plotArea || 0} Sq.ft`}
              />
              <Info
                label="Selected Design"
                value={selected.selectedDesign?.title}
              />
              <Info
                label="Design Size"
                value={selected.selectedDesign?.sizeLabel}
              />
              <Info
                label="Estimate Amount"
                value={`₹ ${selected.estimateAmount || 0}`}
              />
              <Info
                label="Subsidy Amount"
                value={`₹ ${selected.subsidyAmount || 0}`}
              />
              <Info
                label="Society Share"
                value={`₹ ${selected.societyShare || 0}`}
              />
              <Info
                label="Status"
                value={statusLabels[selected.status] || selected.status}
              />
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              {selected.estimatePdfUrl && (
                <a
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                  href={selected.estimatePdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Original Estimate
                </a>
              )}

              {selected.designPdfUrl && (
                <a
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                  href={selected.designPdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Design PDF
                </a>
              )}

              {selected.rnbVerifiedPdfUrl && (
                <a
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                  href={selected.rnbVerifiedPdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  R&B PDF
                </a>
              )}

              <button
                onClick={() => generateOverlayEstimatePDF(selected)}
                className="bg-purple-700 text-white px-4 py-2 rounded"
              >
                Generate PACS Estimate PDF
              </button>

              <button
                onClick={() => generateOfficeReceiptPDF(selected)}
                className="bg-slate-700 text-white px-4 py-2 rounded"
              >
                Receipt PDF
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <Action
                label="Payment Done"
                onClick={() => updateStatus(selected, "PAYMENT_DONE")}
              />
              <Action
                label="Send R&B"
                onClick={() => updateStatus(selected, "RNB_PENDING")}
              />
              <Action
                label="R&B Approved"
                onClick={() => updateStatus(selected, "RNB_APPROVED")}
              />
              <Action
                label="Engineer"
                onClick={() => updateStatus(selected, "ENGINEER_PENDING")}
              />
              <Action
                label="CA"
                onClick={() => updateStatus(selected, "CA_PENDING")}
              />
              <Action
                label="Registrar"
                onClick={() => updateStatus(selected, "REGISTRAR_PENDING")}
              />
              <Action
                label="Subsidy Released"
                onClick={() => updateStatus(selected, "SUBSIDY_RELEASED")}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border rounded-xl p-3 bg-slate-50">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-bold">{value || "-"}</p>
    </div>
  );
}

function Action({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-emerald-600 text-white px-3 py-2 rounded font-bold"
    >
      {label}
    </button>
  );
}